/**
 * USF results in the theme's predictive search drawer.
 *
 * USF stays the search engine (ranking, synonyms, suggestions, collections, pages); the markup is the
 * same as sections/predictive-search.liquid so the theme's own CSS styles it. The drawer element
 * (theme.js: PredictiveSearchDrawer) is untouched apart from where its results come from. If USF is
 * missing or fails, it falls back to Shopify's predictive search.
 */
(function () {
  var configEl = document.getElementById('usf-ps-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  var Drawer = window.customElements && window.customElements.get('predictive-search-drawer');
  if (!Drawer) return;

  // USF binds its popup to search inputs whenever its search results page is on, even with instant
  // search off. Flag the theme's inputs (capture phase, so it also survives USF cloning the input)
  // so USF leaves them to the drawer. USF checks this flag before showing its popup.
  ['focusin', 'click', 'input'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var target = event.target;
      if (target && target.closest && target.closest('.predictive-search__input, #predictive-search-form input')) {
        target._usf_no_popup = true;
      }
    }, true);
  });

  var proto = Drawer.prototype;
  var originalPredictive = proto._doPredictiveSearch;
  var originalLiquid = proto._doLiquidSearch;
  var originalSupport = proto._supportPredictiveApi;
  var strings = config.strings;

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function trimmedRoot() {
    return config.rootUrl === '/' ? '' : config.rootUrl.replace(/\/$/, '');
  }

  function formatMoney(amount) {
    var format = (window.themeVariables && window.themeVariables.settings.moneyFormat) || '{{amount}}';
    var value = Number(amount) || 0;

    function withDelimiters(number, precision, thousands, decimal) {
      var fixed = number.toFixed(precision).split('.');
      var whole = fixed[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return fixed[1] ? whole + decimal + fixed[1] : whole;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, type) {
      switch (type) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'", '.');
        default:
          return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  function imageUrl(product) {
    var image = product.images && product.images[0];
    var url = image && image.url;
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    // Shopify CDN images accept a width parameter; leave anything else untouched.
    if (url.indexOf('cdn.shopify.com') !== -1 || url.indexOf('/cdn/shop/') !== -1) {
      url += (url.indexOf('?') === -1 ? '?' : '&') + 'width=210';
    }
    return url;
  }

  function priceRange(product) {
    var variants = product.variants && product.variants.length ? product.variants : [product];
    var price = Infinity;
    var compare = 0;

    variants.forEach(function (variant) {
      var p = Number(variant.price);
      var c = Number(variant.compareAtPrice);
      if (p < price) price = p;
      if (c > p && c > compare) compare = c;
    });

    if (price === Infinity) price = Number(product.price) || 0;
    return { price: price, compare: compare > price ? compare : 0 };
  }

  function productItem(product) {
    var url = trimmedRoot() + '/products/' + encodeURIComponent(product.urlName);
    var img = imageUrl(product);
    var range = priceRange(product);

    var priceHtml = range.compare
      ? '<span class="price price--highlight">' + formatMoney(range.price) + '</span>' +
        '<span class="price price--compare">' + formatMoney(range.compare) + '</span>'
      : '<span class="price">' + formatMoney(range.price) + '</span>';

    return '<li class="predictive-search__product-item line-item line-item--centered">' +
      '<a href="' + url + '" class="line-item__content-wrapper" data-instant>' +
        (img
          ? '<span class="line-item__image-wrapper"><img class="line-item__image" alt="' + escapeHtml(product.title) +
            '" src="' + escapeHtml(img) + '"></span>'
          : '') +
        '<div class="line-item__info"><div class="product-item-meta">' +
          (config.showVendor && product.vendor
            ? '<span class="product-item-meta__vendor heading heading--xxsmall">' + escapeHtml(product.vendor) + '</span>'
            : '') +
          '<span class="product-item-meta__title text--small">' + escapeHtml(product.title) + '</span>' +
          '<div class="product-item-meta__price-list-container text--small"><div class="price-list">' + priceHtml + '</div></div>' +
        '</div></div>' +
        config.arrowIcon +
      '</a></li>';
  }

  function linkItem(href, label) {
    return '<li class="predictive-search__linklist-item">' +
      '<a class="predictive-search__linklist-link" href="' + escapeHtml(href) + '" data-instant>' +
        '<span>' + escapeHtml(label) + '</span>' + config.arrowIcon +
      '</a></li>';
  }

  function pageUrl(page) {
    var root = trimmedRoot();
    if (page.type === 'Article') return root + '/blogs/' + page.urlName;
    return root + '/pages/' + page.urlName;
  }

  function buildHtml(data, term) {
    var products = (data.items || []).filter(function (p) { return p && p.urlName; });
    var suggestions = data.suggestions || [];
    var pages = data.pages || [];
    var collections = data.collections || [];

    var groups = [];
    if (products.length) {
      groups.push({
        id: 'products',
        label: strings.products,
        html: '<ul class="predictive-search__product-list list--unstyled" role="list" data-type="products">' +
          products.map(productItem).join('') + '</ul>',
        field: 'product'
      });
    }
    if (suggestions.length) {
      groups.push({
        id: 'queries',
        label: strings.queries,
        html: '<ul class="predictive-search__product-list list--unstyled" role="list" data-type="queries">' +
          suggestions.map(function (s) {
            return linkItem(config.searchUrl + '?q=' + encodeURIComponent(s), s);
          }).join('') + '</ul>'
      });
    }
    if (pages.length) {
      groups.push({
        id: 'pages',
        label: strings.pages,
        html: '<ul class="predictive-search__product-list list--unstyled" role="list" data-type="pages">' +
          pages.map(function (p) { return linkItem(pageUrl(p), p.title); }).join('') + '</ul>',
        field: 'page'
      });
    }
    if (collections.length) {
      groups.push({
        id: 'collections',
        label: strings.collections,
        html: '<ul class="predictive-search__product-list list--unstyled" role="list" data-type="collections">' +
          collections.map(function (c) {
            return linkItem(trimmedRoot() + '/collections/' + encodeURIComponent(c.urlName), c.title);
          }).join('') + '</ul>'
      });
    }

    if (!groups.length) {
      return {
        hasResults: false,
        html: '<p class="text--large">' + escapeHtml(strings.noResults) + '</p>' +
          '<div class="button-wrapper"><button type="button" data-action="reset-search" class="button button--primary">' +
          escapeHtml(strings.newSearch) + '</button></div>'
      };
    }

    var tabs = groups.map(function (g, i) {
      return '<button type="button" class="tabs-nav__item heading heading--small" aria-expanded="' + (i === 0) +
        '" aria-controls="predictive-search-' + g.id + '">' + escapeHtml(g.label) + '</button>';
    }).join('');

    var panels = groups.map(function (g, i) {
      return '<div class="predictive-search__results-categories-item" ' + (i === 0 ? '' : 'hidden ') +
        'id="predictive-search-' + g.id + '">' +
        (i === 0 && g.field ? '<input type="hidden" form="predictive-search-form" name="type" value="' + g.field + '">' : '') +
        g.html + '</div>';
    }).join('');

    var collectionsOnly = groups.every(function (g) { return g.id === 'collections'; });

    return {
      hasResults: true,
      html: '<tabs-nav class="tabs-nav tabs-nav--edge2edge tabs-nav--narrow tabs-nav--no-border">' +
          '<scrollable-content class="tabs-nav__scroller hide-scrollbar"><div class="tabs-nav__scroller-inner">' +
          '<div class="tabs-nav__item-list">' + tabs + '</div></div></scrollable-content></tabs-nav>' +
        '<div class="predictive-search__results-categories">' + panels + '</div>' +
        (collectionsOnly ? '<style>[type="submit"][form="predictive-search-form"]{display:none;}</style>' : '')
    };
  }

  function usfRequestUrl(term) {
    var settings = window.usf && window.usf.settings;
    if (!settings || !settings.searchSvcUrl || !settings.siteId) return null;

    var instant = settings.instantSearch || {};
    var params = {
      q: term,
      apiKey: settings.siteId,
      take: instant.numOfProductMatches || 6
    };

    if (instant.showCollections !== false) params.showCollections = 1;
    if (instant.showPages !== false) params.showPages = 1;
    if (window._usfLocale) params.locale = window._usfLocale;
    if (window._usfCustomerTags && window._usfCustomerTags.length) params.customerTags = window._usfCustomerTags.join(',');
    if (window.Shopify && window.Shopify.country) params.country = window.Shopify.country;

    return settings.searchSvcUrl + 'instantsearch?' + Object.keys(params).map(function (key) {
      return key + '=' + encodeURIComponent(params[key]);
    }).join('&');
  }

  async function usfSearch(term, signal) {
    var url = usfRequestUrl(term);
    if (!url) throw new Error('USF is not available');

    var response = await fetch(url, { signal: signal });
    if (!response.ok) throw new Error('USF search failed: ' + response.status);

    var json = await response.json();
    return buildHtml(json.data || json, term);
  }

  async function search(term, fallback) {
    try {
      return await usfSearch(term, this.abortController && this.abortController.signal);
    } catch (e) {
      if (e && e.name === 'AbortError') throw e;
      return fallback.call(this, term);
    }
  }

  // Shopify search to use when USF can't answer, matching what the theme would have picked.
  function shopifyFallback(term) {
    var supported = true;
    try {
      supported = originalSupport.call(this);
    } catch (e) {}
    return (supported ? originalPredictive : originalLiquid).call(this, term);
  }

  proto._doPredictiveSearch = function (term) {
    return search.call(this, term, shopifyFallback);
  };

  proto._doLiquidSearch = proto._doPredictiveSearch;

  // The theme picks between the two methods above; both now go through USF.
  proto._supportPredictiveApi = function () {
    return true;
  };
})();
